import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Leaf } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

export function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location]);

  const links = [
    { to: "/", label: "Home" },
    { to: "/work-with-me", label: "Work With Me" },
    { to: "/journal", label: "Journal" },
    { to: "/about", label: "About" },
    { to: "/blog", label: "Blog" },
    { to: "/contact", label: "Contact" },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header className="fixed top-0 left-0 right-0 z-50 px-6 pt-4" data-testid="navbar">
      <nav
        className={`glass-nav mx-auto max-w-6xl rounded-full px-6 py-3 flex items-center justify-between transition-all duration-300 ${
          scrolled ? "shadow-soft" : ""
        }`}
      >
        <Link to="/" className="flex items-center gap-2 group" data-testid="nav-logo">
          <Leaf className="w-6 h-6 text-navy group-hover:scale-110 transition-transform" />
          <span className="font-heading text-xl font-semibold text-charcoal tracking-tight">
            Journey With Joyce Lim
          </span>
        </Link>

        {/* Desktop links */}
        <div className="hidden md:flex items-center gap-1" data-testid="nav-desktop-links">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, '-')}`}
              className={`px-4 py-2 rounded-full text-sm font-body font-medium transition-all duration-200 ${
                isActive(l.to)
                  ? "bg-navy/10 text-navy"
                  : "text-charcoal/70 hover:text-charcoal hover:bg-champagne-dark/50"
              }`}
            >
              {l.label}
            </Link>
          ))}
        </div>

        <a
          href="https://wa.me/6597946649"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="nav-book-btn"
          className="hidden md:inline-flex bg-navy text-white px-6 py-2.5 rounded-full text-sm font-body font-semibold hover:bg-navy-light transition-all hover:scale-105 shadow-soft"
        >
          Book a Session
        </a>

        {/* Mobile toggle */}
        <button
          onClick={() => setOpen(!open)}
          className="md:hidden p-2 text-charcoal"
          data-testid="nav-mobile-toggle"
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </nav>

      {/* Mobile menu */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="md:hidden glass-nav mx-4 mt-2 rounded-3xl p-6 shadow-lift"
            data-testid="nav-mobile-menu"
          >
            <div className="flex flex-col gap-2">
              {links.map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  data-testid={`nav-mobile-${l.label.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`px-4 py-3 rounded-2xl font-body font-medium transition-all ${
                    isActive(l.to) ? "bg-navy/10 text-navy" : "text-charcoal/70 hover:bg-champagne-dark/50"
                  }`}
                >
                  {l.label}
                </Link>
              ))}
              <a
                href="https://wa.me/6597946649"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="nav-mobile-book-btn"
                className="mt-2 bg-navy text-white px-6 py-3 rounded-full text-center font-body font-semibold"
              >
                Book a Session
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
